import React, { useState, useMemo, useEffect, useCallback, memo } from 'react';
import '../candidates/total.css';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Avatar,
  useMediaQuery,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TablePagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useResignEmployee } from '../../hooks/useCandidate';
import { useAllEmployeeDetails } from '../../hooks/useEmployees';
import EditIcon from '@mui/icons-material/Edit';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import { getMediaUrl } from '../../utils/urlHelper';
import { formatDate } from '../../utils/dateFormat';
import { TH, TH_CENTER, TD, Row, TableWrap, TABLE_STYLE, AVATAR_SX, CHIP_SX } from '../common/tableStyles';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

const SearchField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    '&:hover fieldset': {
      borderColor: ACCENT,
    },
  },
}));

// Soft-tinted row action icon buttons (edit = accent, resign = red).
const EDIT_BTN_SX = { color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } };
const RESIGN_BTN_SX = { color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } };

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// One table row, memoized — only re-renders when its own employee or the
// stable callbacks change. The whole row is a clickable link to the employee's
// profile (`onOpen`); the Edit/Resign icons stop propagation so they stay
// distinct secondary actions. tabIndex/role/onKeyDown give keyboard (Tab+Enter)
// parity with the mouse click.
const EmployeeRow = memo(function EmployeeRow({ applicant, onOpen, onEdit, onResign }) {
  const openProfile = () => onOpen(applicant.employee_no);
  return (
    <Row
      onClick={openProfile}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); openProfile(); } }}
      tabIndex={0}
      role="link"
      aria-label={`View profile of ${applicant.name || 'employee'}`}
      sx={{ cursor: 'pointer' }}
    >
      <td style={TD}>
        <Chip label={applicant.employee_no || 'N/A'} size="small" sx={CHIP_SX} />
      </td>
      <td style={TD}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={getMediaUrl(applicant.photo)} sx={AVATAR_SX}>
            {applicant.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight={600}>{applicant.name || 'N/A'}</Typography>
        </Box>
      </td>
      <td style={TD}>
        <Typography variant="body2">{applicant.designation || 'N/A'}</Typography>
      </td>
      <td style={TD}>
        <Typography variant="body2">{applicant.email || 'N/A'}</Typography>
      </td>
      <td style={TD}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{applicant.department || 'N/A'}</Typography>
        <Typography variant="caption" color="text.secondary">{applicant.branch_name || 'N/A'}</Typography>
      </td>
      <td style={TD}>
        <Typography variant="body2">{applicant.mobile_number || 'N/A'}</Typography>
      </td>
      <td style={TD}>
        <Typography variant="body2">{formatDate(applicant.date_of_joining)}</Typography>
      </td>
      <td style={TD}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Tooltip title="Edit Employee">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(applicant.employee_no); }} sx={EDIT_BTN_SX}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mark Resigned">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onResign(applicant); }} sx={RESIGN_BTN_SX}>
              <PersonOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </td>
    </Row>
  );
});

const Employees = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: emp = [], isFetching } = useAllEmployeeDetails();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebouncedValue(searchInput, 250);
  const [activeFilter, setActiveFilter] = useState('none');
  const [sortType, setSortType] = useState('none');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { mutate: resignEmployee } = useResignEmployee();

  // Filter + sort the full list (cheap; only recomputes when inputs change).
  const processedEmployees = useMemo(() => {
    let result = emp;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      // Digits-only form of the query so a phone search matches regardless of
      // how the stored number is formatted (+91, spaces, dashes). Guarded so a
      // text query (no digits) never falls through and matches every row.
      const digits = searchQuery.replace(/\D/g, '');
      result = result.filter(e =>
        e.name?.toLowerCase().includes(q) ||
        e.employee_no?.toString().includes(searchQuery) ||
        (digits && e.mobile_number?.toString().replace(/\D/g, '').includes(digits))
      );
    }
    if (sortType !== 'none') {
      result = [...result];
      if (sortType === 'date') {
        result.sort((a, b) => new Date(b.date_of_joining) - new Date(a.date_of_joining));
      } else if (sortType === 'name') {
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } else if (sortType === 'id') {
        result.sort((a, b) => a.employee_no - b.employee_no);
      }
    }
    return result;
  }, [emp, searchQuery, sortType]);

  // Only the current page is rendered — this is what removes the load hang.
  const paginatedEmployees = useMemo(
    () => processedEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [processedEmployees, page, rowsPerPage]
  );

  // Snap back to the first page whenever the result set changes.
  useEffect(() => { setPage(0); }, [searchQuery, sortType, rowsPerPage]);

  // Resign Dialog State
  const [resignDialogOpen, setResignDialogOpen] = useState(false);
  const [selectedEmployeeForResign, setSelectedEmployeeForResign] = useState(null);
  const [resignationDate, setResignationDate] = useState('');
  const [resignationReason, setResignationReason] = useState('');
  const [attachments, setAttachments] = useState([]);

  // Opening an employee's profile and editing them land on the same detail page
  // (`/salary/breakdown/:empid` renders the full profile with editable tabs), so
  // the row click and the pencil share one navigation target.
  const handleOpenProfile = useCallback(
    (employeeNo) => navigate(`/salary/breakdown/${employeeNo}`),
    [navigate]
  );
  const handleEdit = handleOpenProfile;

  const handleResign = useCallback((employee) => {
    setSelectedEmployeeForResign(employee);
    setResignationDate(new Date().toISOString().split('T')[0]);
    setResignationReason('');
    setAttachments([]);
    setResignDialogOpen(true);
  }, []);

  const handleConfirmResign = () => {
    if (selectedEmployeeForResign) {
      const formData = new FormData();
      formData.append('resignation_date', resignationDate);
      formData.append('resignation_reason', resignationReason);

      attachments.forEach((file, index) => {
        if (index < 4) {
          formData.append(`file_${index + 1}`, file);
        }
      });

      resignEmployee(
        {
          employeeNo: selectedEmployeeForResign.employee_no,
          formData: formData
        },
        {
          onSuccess: () => {
            setResignDialogOpen(false);
            setSelectedEmployeeForResign(null);
            setAttachments([]);
          }
        }
      );
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (attachments.length + files.length > 4) {
      alert("You can only upload up to 4 files.");
      return;
    }
    setAttachments((prev) => [...prev, ...files].slice(0, 4));
  };

  const handleRemoveFile = (indexToRemove) => {
    setAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSearch = (e) => setSearchInput(e.target.value);

  const sortedData = (type) => {
    setSortType(type);
    if (type === 'date') setActiveFilter('Joining Date');
    else if (type === 'name') setActiveFilter('Name');
    else if (type === 'id') setActiveFilter('ID');
    else setActiveFilter('none');
    handleClose();
  };

  const downloadExcel = () => {
    const rawData = processedEmployees.map(emp => ({
      'Emp No': emp.employee_no,
      'Name': emp.name,
      'Designation': emp.designation,
      'Email': emp.email,
      'Mobile': emp.mobile_number,
      'Joining Date': emp.date_of_joining
    }));
    const worksheet = XLSX.utils.json_to_sheet(rawData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
    XLSX.writeFile(workbook, 'employee_data.xlsx');
  };

  const renderMobileCard = (employee) => (
    <Card
      key={employee.id || employee.employee_no}
      elevation={0}
      onClick={() => handleOpenProfile(employee.employee_no)}
      role="link"
      aria-label={`View profile of ${employee.name || 'employee'}`}
      sx={{
        ...CARD_SX,
        mb: 2,
        p: 1,
        cursor: 'pointer',
        '&:hover': { boxShadow: '0 12px 28px rgba(17,24,39,0.08)' }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={getMediaUrl(employee.photo)}
            sx={{ bgcolor: 'primary.main', width: 48, height: 48, mr: 2 }}
          >
            {employee.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>{employee.name || 'N/A'}</Typography>
            <Typography variant="body2">{employee.designation || 'N/A'}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2">#{employee.employee_no}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2">{employee.email || 'N/A'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Department:</Typography>
            <Typography variant="body2">{employee.department || 'N/A'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Branch:</Typography>
            <Typography variant="body2">{employee.branch_name || 'N/A'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Joined:</Typography>
            <Typography variant="body2">{formatDate(employee.date_of_joining)}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(employee.employee_no); }} sx={EDIT_BTN_SX}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleResign(employee); }} sx={RESIGN_BTN_SX}>
            <PersonOffIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  const pagination = processedEmployees.length > 0 && (
    <TablePagination
      component="div"
      count={processedEmployees.length}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage="Rows:"
    />
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PersonIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  Employees
                </Typography>
                <Chip
                  label={processedEmployees.length}
                  sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: '#eef4ff', color: ACCENT, border: 'none' }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Directory of all employees across the organization
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClick}
              startIcon={<FilterAltIcon />}
              sx={OUTLINED_BTN}
            >
              {activeFilter !== 'none' ? activeFilter : 'Filter'}
            </Button>

            <Button
              variant="contained"
              disableElevation
              onClick={downloadExcel}
              startIcon={<DownloadIcon />}
              sx={GRADIENT_BTN}
            >
              Export
            </Button>

            <SearchField
              placeholder="Search employee..."
              value={searchInput}
              onChange={handleSearch}
              size="small"
              sx={{ minWidth: { sm: '280px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
        {isFetching && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
          </Box>
        )}
      </Paper>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={() => sortedData('date')} sx={{ '&:hover': { bgcolor: '#f5f5f5' }, py: 1.5, px: 2 }}>
            Filter by Joining Date
          </MenuItem>
          <MenuItem onClick={() => sortedData('name')} sx={{ '&:hover': { bgcolor: '#f5f5f5' }, py: 1.5, px: 2 }}>
            Filter by Name
          </MenuItem>
          <MenuItem onClick={() => sortedData('id')} sx={{ '&:hover': { bgcolor: '#f5f5f5' }, py: 1.5, px: 2 }}>
            Filter by ID
          </MenuItem>
        </Menu>

        {processedEmployees?.length > 0 ? (
          <>
            {isMobile ? (
              <Box sx={{ maxHeight: '70vh', overflowY: 'auto', p: 1 }}>
                {paginatedEmployees.map((employee) => renderMobileCard(employee))}
                {pagination}
              </Box>
            ) : (
              <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.5, md: 2 } }}>
                <TableWrap>
                  <table style={TABLE_STYLE}>
                    <thead>
                      <tr>
                        <th style={TH}>Emp.No</th>
                        <th style={TH}>Name</th>
                        <th style={TH}>Role</th>
                        <th style={TH}>Email</th>
                        <th style={TH}>Dept/Branch</th>
                        <th style={TH}>Contact</th>
                        <th style={TH}>Joining Date</th>
                        <th style={TH_CENTER}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEmployees.map((applicant) => (
                        <EmployeeRow
                          key={applicant.id || applicant.employee_no}
                          applicant={applicant}
                          onOpen={handleOpenProfile}
                          onEdit={handleEdit}
                          onResign={handleResign}
                        />
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
                {pagination}
              </Paper>
            )}
          </>
        ) : (
          <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 8 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonIcon />
            </Box>
            <Typography variant="h6" sx={{ color: '#111827', fontWeight: 700 }}>No Employees Data</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Employees data will appear here once available.</Typography>
          </Paper>
        )}
      {/* Resignation Dialog */}
      <Dialog open={resignDialogOpen} onClose={() => setResignDialogOpen(false)} PaperProps={{ sx: { borderRadius: '22px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#111827' }}>Resign Employee</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: '400px' }}>
            <Typography variant="body1">
              Resigning <strong>{selectedEmployeeForResign?.name}</strong> (#{selectedEmployeeForResign?.employee_no})
            </Typography>
            <TextField
              label="Resignation Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={resignationDate}
              onChange={(e) => setResignationDate(e.target.value)}
            />
            <TextField
              label="Reason for Resignation"
              multiline
              rows={3}
              fullWidth
              value={resignationReason}
              onChange={(e) => setResignationReason(e.target.value)}
              placeholder="Enter reason..."
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Attachments (Optional, up to 4 PDFs/Images)
              </Typography>
              <input
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                id="resignation-file-upload"
                multiple
                type="file"
                onChange={handleFileChange}
                disabled={attachments.length >= 4}
              />
              <label htmlFor="resignation-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={attachments.length >= 4}
                  size="small"
                  sx={OUTLINED_BTN}
                >
                  Choose Files
                </Button>
              </label>

              {attachments.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {attachments.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => handleRemoveFile(index)}
                      size="small"
                      sx={{ maxWidth: '200px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResignDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>Cancel</Button>
          <Button
            onClick={handleConfirmResign}
            color="error"
            variant="contained"
            disableElevation
            disabled={!resignationDate}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', px: 3 }}
          >
            Resign Employee
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Employees;

// Styles for table headers
export const tableHeaderStyle = {
  padding: "8px",
  backgroundColor: "#212529",
  color: "white",
};
